/**
 * Shared offering-status helpers for Builder section components.
 *
 * The V2 primary_facade exposes four status values that unambiguously
 * mean **Test The Waters** (the visitor should see the orange "Reserve
 * Now" CTA):
 *
 *   - `'testing'`            — legacy Reg CF TTW value. The backend
 *                              (`Startup#status` Mongoid field, see
 *                              `app/models/startup.rb`) sets `ttw_date`
 *                              the moment a Reg CF offering transitions
 *                              into this status.
 *   - `'test_the_waters'`    — current Reg A+ TTW value from the V2 facade.
 *   - `'pre_launch'`         — Reg A+ qualified but not yet open.
 *   - `'ttw'`                — short form returned by some endpoints.
 *
 * `'not_submitted'` is **intentionally absent** from this list. It
 * literally means the issuer hasn't submitted yet, which covers two
 * indistinguishable cases from the API:
 *
 *   1. A live Reg CF / Reg A+ pre-Form-C / pre-qualification TTW
 *      campaign (e.g. gacw-reg-a, GACW Reg A+ Jun 2026 — disclosure
 *      text says "TESTING THE WATERS UNDER TIER 2 OF REGULATION A"
 *      but the V2 payload exposes no `ttwDate`, no `numberOfHoursLeft`,
 *      no explicit TTW flag).
 *   2. A brand-new offering still being drafted / previewed by the
 *      issuer or admin (e.g. heartstone-cf-2, cancervax-cf2 admin
 *      previews — Isabella Marklin Jun 10 2026).
 *
 * Auto-detecting either case from the BE shape would mis-handle the
 * other. Each Builder section that owns a CTA exposes a
 * `forceReserveCta` (a.k.a. "TTW Mode") toggle that admins / issuers
 * flip on when they intentionally want the orange Reserve Now button —
 * see `BuilderHeroSection`, `BuilderClassicInvestSidebar`,
 * `BuilderClassicTwoColumnLayout`, and `BuilderGetEquityBubble`. They
 * combine `isTtwOffering(offeringData) || Boolean(forceReserveCta)`
 * before deciding the button copy / color / redirect path.
 *
 * Mirrors `TTW_STATUSES` in `src/utils/builderTargeting.ts` (used for
 * Builder.io page-type derivation). Lowercased before comparison so
 * backend casing drift doesn't silently break the check (CodeRabbit'd
 * around PD-34457).
 */

const TTW_BUTTON_STATUSES = new Set(['testing', 'test_the_waters', 'pre_launch', 'ttw']);

const REG_D_REGULATION_TYPES = new Set(['reg d', '506c', '506(c)']);

const isRegDRegulation = (regulationType) =>
  typeof regulationType === 'string' && REG_D_REGULATION_TYPES.has(regulationType.toLowerCase());

/**
 * Pure status check — returns true for any of the V2 TTW status values.
 * Use this when you only have a status string and no regulation context
 * (e.g. for cache-key derivation, page-type detection). For deciding
 * whether a campaign-page invest button should read "Reserve Now" vs
 * "Get Equity"/"Get Shares Now", prefer `isTtwOffering` below — it
 * also accounts for Reg D, which never has a TTW phase.
 */
export const isTtwStatus = (status) =>
  typeof status === 'string' && TTW_BUTTON_STATUSES.has(status.toLowerCase());

/**
 * "Should this offering's invest button auto-render as Reserve Now?"
 *
 * Returns true only when the offering is in one of the four
 * unambiguous Reg CF / Reg A+ TTW statuses. Reg D / 506(c) offerings
 * are excluded outright — those use `not_submitted` to mean "private,
 * pre-launch", not TTW (Hannah Minn / Consensys regression Jun 2 2026).
 *
 * `not_submitted` is **never** auto-TTW because the BE can't
 * distinguish a real pre-Form-C TTW campaign from an unfinished draft
 * preview (see file-level comment). Use the `forceReserveCta` toggle
 * on the CTA-owning Builder sections to opt in to Reserve Now mode for
 * those offerings.
 *
 * Pass an `offeringData` object (or `{ status, regulationType }`
 * shape). Returns false for missing/falsy input so the button-rendering
 * code doesn't fire its TTW branch on undefined data either.
 */
export const isTtwOffering = (offeringData) => {
  if (!offeringData) return false;
  if (isRegDRegulation(offeringData.regulationType)) return false;
  return isTtwStatus(offeringData.status);
};
